import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, DollarSign, Users, Target, Award, Clock, CheckCircle, X, Settings, Filter, CalendarIcon, BarChart3 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { filterJobsByPermissions } from '../../utils/permissions';
import { CompetitionZoneWidget } from '../../components/widgets/CompetitionZoneWidget';
import { RhinoChallengesWidget } from '../../components/widgets/RhinoChallengesWidget';
import { SavedViewsDropdown } from '../../components/SavedViewsDropdown';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// KPI Card Component - moved outside to avoid recreation on every render
const KPICard = ({ title, current, previous, change, isCurrency = false }) => {
  const isPositive = parseFloat(change) >= 0;
  const displayCurrent = isCurrency ? formatCurrency(current) : current;
  const displayPrevious = isCurrency ? formatCurrency(previous) : previous;
  
  return (
    <div className="bg-slate-800/80 dark:bg-slate-800/80 rounded-lg p-6 border border-slate-700 hover:border-slate-600 hover:shadow-lg transition-all">
      <div className="text-sm font-medium text-slate-400 dark:text-slate-400 mb-3 uppercase tracking-wide">{title}</div>
      <div className="text-3xl font-bold text-white dark:text-white mb-2">{displayCurrent}</div>
      <div className="flex items-center gap-2 text-sm">
        <span className={`flex items-center gap-1 ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
          {isPositive ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
          {displayPrevious} ({Math.abs(change)}%)
        </span>
      </div>
    </div>
  );
};

export const SalesDashboard = () => {
  const { user, viewAsRole, viewAsUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    totalLeads: 0,
    activeOpportunities: 0,
    conversionRate: 0,
    totalValue: 0,
    avgDealSize: 0,
    todayLeads: 0,
    weekLeads: 0,
    monthLeads: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [timeRange, setTimeRange] = useState('month'); // today, week, month, quarter, year
  const [selectedDate, setSelectedDate] = useState('today');
  const [comparisonDate, setComparisonDate] = useState('yesterday');
  const [selectedMarket, setSelectedMarket] = useState('all');
  const [selectedRep, setSelectedRep] = useState('all');
  const [markets, setMarkets] = useState([]);
  // New EXTEND features
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [compareQuarter, setCompareQuarter] = useState('none'); // none, q1, q2, q3, q4
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [kpiStats, setKpiStats] = useState({
    newOpportunities: { current: 0, previous: 0, change: 0 },
    appointmentsSet: { current: 0, previous: 0, change: 0 },
    leadsIssued: { current: 0, previous: 0, change: 0 },
    demos: { current: 0, previous: 0, change: 0 },
    jobsSold: { current: 0, previous: 0, change: 0 },
    paymentsReceived: { current: 0, previous: 0, change: 0 },
    soldJobsAmount: { current: 0, previous: 0, change: 0 },
    revenueAmount: { current: 0, previous: 0, change: 0 }
  });
  const [showTopGoals, setShowTopGoals] = useState(true);

  useEffect(() => {
    fetchData();
  }, [timeRange]);

  // Apply role-based filtering when data changes or filters change
  useEffect(() => {
    applyRoleBasedFilter();
  }, [allJobs, user, viewAsRole, viewAsUser, users, timeRange, selectedMarket, selectedRep, includeCancelled, compareQuarter, customDateRange]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [jobsRes, usersRes, marketsRes] = await Promise.all([
        axios.get(`${API}/jobs`, { headers }),
        axios.get(`${API}/users`, { headers }),
        axios.get(`${API}/markets`, { headers })
      ]);
      
      setAllJobs(jobsRes.data);
      setUsers(usersRes.data);
      setMarkets(marketsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const applyRoleBasedFilter = () => {
    if (!allJobs || allJobs.length === 0) {
      setJobs([]);
      return;
    }

    // Determine the effective user
    let effectiveUser = user;

    // If testing as a different role/user
    if (viewAsUser && viewAsRole) {
      const testUser = users.find(u => u.id === viewAsUser);
      if (testUser) {
        effectiveUser = testUser;
      }
    }

    // Use the comprehensive permissions system
    let filteredJobs = filterJobsByPermissions(allJobs, effectiveUser, users);
    
    // Apply Include Cancelled filter - EXTEND ITEM #1
    if (!includeCancelled) {
      filteredJobs = filteredJobs.filter(job => 
        job.stage !== 'cancelled' && job.status !== 'cancelled'
      );
    }
    
    // Apply market filter
    if (selectedMarket && selectedMarket !== 'all') {
      filteredJobs = filteredJobs.filter(job => job.market_id === selectedMarket);
    }
    
    // Apply sales rep filter
    if (selectedRep && selectedRep !== 'all') {
      filteredJobs = filteredJobs.filter(job => job.assigned_to === selectedRep);
    }
    
    setJobs(filteredJobs);
    calculateStats(filteredJobs);
    extractRecentActivity(filteredJobs);
  };

  const calculateStats = (jobsData) => {
    const salesStages = ['lead', 'intro_call', 'appointment_set', 'demo', 'pending_signature', 'signed_agreement'];
    const salesJobs = jobsData.filter(job => salesStages.includes(job.stage));
    
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);
    
    // Calculate starts based on time range
    let currentPeriodStart, previousPeriodStart, previousPeriodEnd;
    
    // EXTEND ITEM #2 - Quarter Comparison Support
    if (compareQuarter !== 'none') {
      const quarterMap = { q1: 0, q2: 1, q3: 2, q4: 3 };
      const quarterNum = quarterMap[compareQuarter];
      const currentQuarter = Math.floor(now.getMonth() / 3);
      
      // Current period is the selected quarter
      currentPeriodStart = new Date(now.getFullYear(), quarterNum * 3, 1);
      previousPeriodStart = new Date(now.getFullYear() - 1, quarterNum * 3, 1);
      previousPeriodEnd = new Date(now.getFullYear(), (quarterNum + 1) * 3, 0);
    } else if (customDateRange.from && customDateRange.to) {
      // Custom date range mode
      currentPeriodStart = new Date(customDateRange.from);
      const rangeDays = Math.ceil((customDateRange.to - customDateRange.from) / (1000 * 60 * 60 * 24));
      previousPeriodStart = new Date(customDateRange.from);
      previousPeriodStart.setDate(previousPeriodStart.getDate() - rangeDays);
      previousPeriodEnd = new Date(customDateRange.from);
    } else if (timeRange === 'today') {
      currentPeriodStart = todayStart;
      previousPeriodStart = yesterdayStart;
      previousPeriodEnd = todayStart;
    } else if (timeRange === 'week') {
      currentPeriodStart = new Date(now);
      currentPeriodStart.setDate(now.getDate() - now.getDay());
      previousPeriodStart = new Date(currentPeriodStart);
      previousPeriodStart.setDate(previousPeriodStart.getDate() - 7);
      previousPeriodEnd = currentPeriodStart;
    } else if (timeRange === 'month') {
      currentPeriodStart = new Date(now.getFullYear(), now.getMonth(), 1);
      previousPeriodStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      previousPeriodEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    } else if (timeRange === 'quarter') {
      const currentQuarter = Math.floor(now.getMonth() / 3);
      currentPeriodStart = new Date(now.getFullYear(), currentQuarter * 3, 1);
      previousPeriodStart = new Date(now.getFullYear(), (currentQuarter - 1) * 3, 1);
      previousPeriodEnd = new Date(now.getFullYear(), currentQuarter * 3, 0);
    } else if (timeRange === 'year') {
      currentPeriodStart = new Date(now.getFullYear(), 0, 1);
      previousPeriodStart = new Date(now.getFullYear() - 1, 0, 1);
      previousPeriodEnd = new Date(now.getFullYear(), 0, 0);
    } else {
      // Default to month
      currentPeriodStart = new Date(now.getFullYear(), now.getMonth(), 1);
      previousPeriodStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      previousPeriodEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    }
    
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - now.getDay());
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    
    const totalLeads = salesJobs.length;
    const activeOpportunities = salesJobs.filter(j => j.stage !== 'lead').length;
    const signedJobs = jobsData.filter(j => j.stage === 'signed_agreement').length;
    const conversionRate = totalLeads > 0 ? ((signedJobs / totalLeads) * 100).toFixed(1) : 0;
    
    const totalValue = salesJobs.reduce((sum, job) => sum + (job.estimated_value || 0), 0);
    const avgDealSize = totalLeads > 0 ? (totalValue / totalLeads).toFixed(0) : 0;
    
    const todayLeads = salesJobs.filter(j => new Date(j.created_at) >= todayStart).length;
    const weekLeads = salesJobs.filter(j => new Date(j.created_at) >= weekStart).length;
    const monthLeads = salesJobs.filter(j => new Date(j.created_at) >= monthStart).length;
    
    // Calculate KPI stats with comparison based on selected time range
    const currentPeriodJobs = salesJobs.filter(j => new Date(j.created_at) >= currentPeriodStart);
    
    const previousPeriodJobs = salesJobs.filter(j => {
      const createdDate = new Date(j.created_at);
      return createdDate >= previousPeriodStart && createdDate < (previousPeriodEnd || currentPeriodStart);
    });
    
    const calculateChange = (current, previous) => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return (((current - previous) / previous) * 100).toFixed(0);
    };
    
    const newOpportunitiesCurrent = currentPeriodJobs.filter(j => j.stage === 'lead' || j.stage === 'intro_call').length;
    const newOpportunitiesPrevious = previousPeriodJobs.filter(j => j.stage === 'lead' || j.stage === 'intro_call').length;
    
    const appointmentsCurrent = currentPeriodJobs.filter(j => j.stage === 'appointment_set').length;
    const appointmentsPrevious = previousPeriodJobs.filter(j => j.stage === 'appointment_set').length;
    
    const demosCurrent = currentPeriodJobs.filter(j => j.stage === 'demo').length;
    const demosPrevious = previousPeriodJobs.filter(j => j.stage === 'demo').length;
    
    const soldCurrent = currentPeriodJobs.filter(j => j.stage === 'signed_agreement' || j.stage === 'qc' || j.stage === 'production').length;
    const soldPrevious = previousPeriodJobs.filter(j => j.stage === 'signed_agreement' || j.stage === 'qc' || j.stage === 'production').length;
    
    const soldAmountCurrent = currentPeriodJobs.filter(j => j.stage === 'signed_agreement' || j.stage === 'qc' || j.stage === 'production').reduce((sum, j) => sum + (j.estimated_value || j.contract_amount || 0), 0);
    const soldAmountPrevious = previousPeriodJobs.filter(j => j.stage === 'signed_agreement' || j.stage === 'qc' || j.stage === 'production').reduce((sum, j) => sum + (j.estimated_value || j.contract_amount || 0), 0);
    
    setKpiStats({
      newOpportunities: {
        current: newOpportunitiesCurrent,
        previous: newOpportunitiesPrevious,
        change: calculateChange(newOpportunitiesCurrent, newOpportunitiesPrevious)
      },
      appointmentsSet: {
        current: appointmentsCurrent,
        previous: appointmentsPrevious,
        change: calculateChange(appointmentsCurrent, appointmentsPrevious)
      },
      leadsIssued: {
        current: currentPeriodJobs.length,
        previous: previousPeriodJobs.length,
        change: calculateChange(currentPeriodJobs.length, previousPeriodJobs.length)
      },
      demos: {
        current: demosCurrent,
        previous: demosPrevious,
        change: calculateChange(demosCurrent, demosPrevious)
      },
      jobsSold: {
        current: soldCurrent,
        previous: soldPrevious,
        change: calculateChange(soldCurrent, soldPrevious)
      },
      paymentsReceived: {
        current: soldCurrent, // Using sold count as proxy for payments
        previous: soldPrevious,
        change: calculateChange(soldCurrent, soldPrevious)
      },
      soldJobsAmount: {
        current: soldAmountCurrent,
        previous: soldAmountPrevious,
        change: calculateChange(soldAmountCurrent, soldAmountPrevious)
      },
      revenueAmount: {
        current: soldAmountCurrent,
        previous: soldAmountPrevious,
        change: calculateChange(soldAmountCurrent, soldAmountPrevious)
      }
    });
    
    setStats({
      totalLeads,
      activeOpportunities,
      conversionRate,
      totalValue,
      avgDealSize,
      todayLeads,
      weekLeads,
      monthLeads
    });
  };

  const extractRecentActivity = (jobsData) => {
    const activities = [];
    
    jobsData.forEach(job => {
      if (job.activity_log && job.activity_log.length > 0) {
        job.activity_log.forEach(log => {
          activities.push({
            ...log,
            jobId: job.id,
            customerName: job.customer_name,
            jobType: job.job_type
          });
        });
      }
    });
    
    // Sort by timestamp and take latest 10
    const sorted = activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    setRecentActivity(sorted.slice(0, 10));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Helper function to get current filters as an object
  const getCurrentFilters = () => ({
    timeRange,
    selectedDate,
    comparisonDate,
    selectedMarket,
    selectedRep,
    includeCancelled,
    compareQuarter,
    customDateRange,
    showCustomRange
  });

  // Handler to apply a loaded view's filters
  const handleLoadView = (filters) => {
    if (filters.timeRange) setTimeRange(filters.timeRange);
    if (filters.selectedDate) setSelectedDate(filters.selectedDate);
    if (filters.comparisonDate) setComparisonDate(filters.comparisonDate);
    if (filters.selectedMarket) setSelectedMarket(filters.selectedMarket);
    if (filters.selectedRep) setSelectedRep(filters.selectedRep);
    if (filters.includeCancelled !== undefined) setIncludeCancelled(filters.includeCancelled);
    if (filters.compareQuarter) setCompareQuarter(filters.compareQuarter);
    if (filters.customDateRange) setCustomDateRange(filters.customDateRange);
    if (filters.showCustomRange !== undefined) setShowCustomRange(filters.showCustomRange);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground dark:text-white">Sales Dashboard</h1>
            <p className="text-muted-foreground dark:text-slate-400">Track your sales performance and pipeline</p>
          </div>
          <SavedViewsDropdown
            dashboardType="sales"
            currentFilters={getCurrentFilters()}
            onLoadView={handleLoadView}
          />
        </div>

        {/* Filters Section - Dark Mode with Blue Glow */}
        <Card 
          className="dark:bg-slate-800/60 border-2 border-blue-500 dark:border-blue-400 transition-all duration-300 hover:border-blue-400"
          style={{
            boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)'
          }}
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="h-5 w-5 text-blue-500 dark:text-blue-400" />
              <h3 className="text-lg font-semibold text-foreground dark:text-white">Filters & Time Range</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Time Range Selector */}
              <div>
                <label className="block text-sm font-medium text-foreground dark:text-slate-200 mb-2">
                  Time Period
                </label>
                <Select value={timeRange} onValueChange={(val) => { setTimeRange(val); setCompareQuarter('none'); setShowCustomRange(false); }}>
                  <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600 dark:text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="week">This Week</SelectItem>
                    <SelectItem value="month">This Month</SelectItem>
                    <SelectItem value="quarter">This Quarter</SelectItem>
                    <SelectItem value="year">This Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* EXTEND ITEM #2 - Quarter Comparison */}
              <div>
                <label className="block text-sm font-medium text-foreground dark:text-slate-200 mb-2">
                  Compare Quarter
                </label>
                <Select value={compareQuarter} onValueChange={(val) => { setCompareQuarter(val); if (val !== 'none') setShowCustomRange(false); }}>
                  <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600 dark:text-white">
                    <SelectValue placeholder="Select Quarter" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                    <SelectItem value="none">No Comparison</SelectItem>
                    <SelectItem value="q1">Q1 (Jan-Mar)</SelectItem>
                    <SelectItem value="q2">Q2 (Apr-Jun)</SelectItem>
                    <SelectItem value="q3">Q3 (Jul-Sep)</SelectItem>
                    <SelectItem value="q4">Q4 (Oct-Dec)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Custom Date Range */}
              <div>
                <label className="block text-sm font-medium text-foreground dark:text-slate-200 mb-2">
                  Custom Range
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-normal dark:bg-slate-700 dark:border-slate-600 dark:text-white ${!customDateRange.from && 'text-muted-foreground'}`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {customDateRange.from ? (
                        customDateRange.to ? (
                          `${format(customDateRange.from, 'MMM d')} - ${format(customDateRange.to, 'MMM d')}`
                        ) : format(customDateRange.from, 'MMM d, yyyy')
                      ) : (
                        <span>Pick dates</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 dark:bg-slate-800" align="start">
                    <Calendar
                      mode="range"
                      selected={customDateRange}
                      onSelect={(range) => {
                        setCustomDateRange(range || { from: null, to: null });
                        if (range?.from && range?.to) {
                          setCompareQuarter('none');
                        }
                      }}
                      numberOfMonths={2}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Market Filter */}
              <div>
                <label className="block text-sm font-medium text-foreground dark:text-slate-200 mb-2">
                  Market
                </label>
                <Select value={selectedMarket} onValueChange={setSelectedMarket}>
                  <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600 dark:text-white">
                    <SelectValue placeholder="All Markets" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                    <SelectItem value="all">All Markets</SelectItem>
                    {markets.map(market => (
                      <SelectItem key={market.id} value={market.id}>
                        {market.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sales Rep Filter */}
              <div>
                <label className="block text-sm font-medium text-foreground dark:text-slate-200 mb-2">
                  Sales Rep
                </label>
                <Select value={selectedRep} onValueChange={setSelectedRep}>
                  <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600 dark:text-white">
                    <SelectValue placeholder="All Reps" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                    <SelectItem value="all">All Sales Reps</SelectItem>
                    {users
                      .filter(u => ['sales', 'sales_manager', 'sales_rep', 'director_of_sales'].includes(u.role))
                      .map(rep => (
                        <SelectItem key={rep.id} value={rep.id}>
                          {rep.first_name} {rep.last_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* EXTEND ITEM #1 - Include Cancelled Toggle + Active Filters */}
            <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-slate-700">
              {/* Include Cancelled Toggle */}
              <div className="flex items-center gap-2">
                <Switch 
                  id="include-cancelled"
                  checked={includeCancelled}
                  onCheckedChange={setIncludeCancelled}
                />
                <Label htmlFor="include-cancelled" className="text-sm dark:text-slate-200 cursor-pointer">
                  Include Cancelled Projects
                </Label>
                {includeCancelled && (
                  <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/30">
                    +{allJobs.filter(j => j.stage === 'cancelled' || j.status === 'cancelled').length} cancelled
                  </Badge>
                )}
              </div>
              
              {/* Active Filters Display */}
              <div className="flex flex-wrap gap-2 ml-auto">
                {compareQuarter !== 'none' && (
                  <Badge variant="secondary" className="flex items-center gap-1 bg-purple-500/20 text-purple-300 border-purple-500/30">
                    <BarChart3 className="h-3 w-3" />
                    {compareQuarter.toUpperCase()} Comparison
                    <X 
                      className="h-3 w-3 cursor-pointer" 
                      onClick={() => setCompareQuarter('none')}
                    />
                  </Badge>
                )}
                {customDateRange.from && customDateRange.to && (
                  <Badge variant="secondary" className="flex items-center gap-1 bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                    <CalendarIcon className="h-3 w-3" />
                    {format(customDateRange.from, 'MMM d')} - {format(customDateRange.to, 'MMM d')}
                    <X 
                      className="h-3 w-3 cursor-pointer" 
                      onClick={() => setCustomDateRange({ from: null, to: null })}
                    />
                  </Badge>
                )}
                {selectedMarket !== 'all' && (
                  <Badge variant="secondary" className="flex items-center gap-1 dark:bg-slate-700 dark:text-slate-200">
                    {markets.find(m => m.id === selectedMarket)?.name || 'Market'}
                    <X 
                      className="h-3 w-3 cursor-pointer" 
                      onClick={() => setSelectedMarket('all')}
                    />
                  </Badge>
                )}
                {selectedRep !== 'all' && (
                  <Badge variant="secondary" className="flex items-center gap-1 dark:bg-slate-700 dark:text-slate-200">
                    {users.find(u => u.id === selectedRep)?.first_name || 'Rep'}
                    <X 
                      className="h-3 w-3 cursor-pointer" 
                      onClick={() => setSelectedRep('all')}
                    />
                  </Badge>
                )}
                {(selectedMarket !== 'all' || selectedRep !== 'all' || compareQuarter !== 'none' || (customDateRange.from && customDateRange.to)) && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="dark:text-slate-300 dark:hover:text-white"
                    onClick={() => {
                      setSelectedMarket('all');
                      setSelectedRep('all');
                      setCompareQuarter('none');
                      setCustomDateRange({ from: null, to: null });
                    }}
                  >
                    Clear All
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Stats for CEO - Dark Mode with Blue Glow */}
        {user && ['ceo', 'president', 'president_&_ceo', 'super_admin', 'director_of_sales', 'general_manager'].includes(user.role) && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card 
              className="dark:bg-slate-800/60 border-2 border-blue-500 dark:border-blue-400 transition-all duration-300 hover:border-blue-400"
              style={{
                boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)'
              }}
            >
              <CardContent className="pt-6">
                <div className="text-sm text-muted-foreground dark:text-slate-400">Total Jobs in System</div>
                <div className="text-3xl font-bold text-blue-500 dark:text-blue-400">{allJobs.length}</div>
              </CardContent>
            </Card>
            <Card 
              className="dark:bg-slate-800/60 border-2 border-blue-500 dark:border-blue-400 transition-all duration-300 hover:border-blue-400"
              style={{
                boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)'
              }}
            >
              <CardContent className="pt-6">
                <div className="text-sm text-muted-foreground dark:text-slate-400">Active in Sales Pipeline</div>
                <div className="text-3xl font-bold text-emerald-500 dark:text-emerald-400">{jobs.length}</div>
              </CardContent>
            </Card>
            <Card 
              className="dark:bg-slate-800/60 border-2 border-blue-500 dark:border-blue-400 transition-all duration-300 hover:border-blue-400"
              style={{
                boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)'
              }}
            >
              <CardContent className="pt-6">
                <div className="text-sm text-muted-foreground dark:text-slate-400">Total Markets</div>
                <div className="text-3xl font-bold text-purple-500 dark:text-purple-400">{stats.totalLeads}</div>
              </CardContent>
            </Card>
            <Card 
              className="dark:bg-slate-800/60 border-2 border-blue-500 dark:border-blue-400 transition-all duration-300 hover:border-blue-400"
              style={{
                boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)'
              }}
            >
              <CardContent className="pt-6">
                <div className="text-sm text-muted-foreground dark:text-slate-400">Conversion Rate</div>
                <div className="text-3xl font-bold text-orange-500 dark:text-orange-400">{stats.conversionRate}%</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Original KPI Cards Section (Quick Stats) - Dark Mode with Blue Glow - MOVED ABOVE KPIs */}
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-foreground dark:text-white">Quick Stats</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setTimeRange('today')}
              className={`px-4 py-2 rounded-lg transition-colors ${timeRange === 'today' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
            >
              Today
            </button>
            <button
              onClick={() => setTimeRange('week')}
              className={`px-4 py-2 rounded-lg transition-colors ${timeRange === 'week' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
            >
              Week
            </button>
            <button
              onClick={() => setTimeRange('month')}
              className={`px-4 py-2 rounded-lg transition-colors ${timeRange === 'month' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
            >
              Month
            </button>
          </div>
        </div>

        {/* KPI Cards - Dark Mode with Blue Glow */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total Leads */}
          <Card 
            className="dark:bg-slate-800/60 border-2 border-blue-500 dark:border-blue-400 transition-all duration-300 hover:border-blue-400"
            style={{ boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)' }}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium dark:text-slate-200">Total Leads</CardTitle>
              <Users className="h-4 w-4 text-blue-500 dark:text-blue-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold dark:text-white">{stats.totalLeads}</div>
              <p className="text-xs text-muted-foreground dark:text-slate-400 mt-1">
                {timeRange === 'today' && `${stats.todayLeads} today`}
                {timeRange === 'week' && `${stats.weekLeads} this week`}
                {timeRange === 'month' && `${stats.monthLeads} this month`}
              </p>
            </CardContent>
          </Card>

          {/* Active Opportunities */}
          <Card 
            className="dark:bg-slate-800/60 border-2 border-blue-500 dark:border-blue-400 transition-all duration-300 hover:border-blue-400"
            style={{ boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)' }}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium dark:text-slate-200">Active Opportunities</CardTitle>
              <Target className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold dark:text-white">{stats.activeOpportunities}</div>
              <p className="text-xs text-muted-foreground dark:text-slate-400 mt-1">In sales process</p>
            </CardContent>
          </Card>

          {/* Pipeline Value */}
          <Card 
            className="dark:bg-slate-800/60 border-2 border-blue-500 dark:border-blue-400 transition-all duration-300 hover:border-blue-400"
            style={{ boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)' }}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium dark:text-slate-200">Pipeline Value</CardTitle>
              <DollarSign className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold dark:text-white">{formatCurrency(stats.totalValue)}</div>
              <p className="text-xs text-muted-foreground dark:text-slate-400 mt-1">Total estimated value</p>
            </CardContent>
          </Card>

          {/* Conversion Rate */}
          <Card 
            className="dark:bg-slate-800/60 border-2 border-blue-500 dark:border-blue-400 transition-all duration-300 hover:border-blue-400"
            style={{ boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)' }}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium dark:text-slate-200">Conversion Rate</CardTitle>
              <Award className="h-4 w-4 text-purple-500 dark:text-purple-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold dark:text-white">{stats.conversionRate}%</div>
              <p className="text-xs text-muted-foreground dark:text-slate-400 mt-1">Avg: {formatCurrency(stats.avgDealSize)} per deal</p>
            </CardContent>
          </Card>
        </div>

        {/* Key Performance Indicators Section - Dark Mode */}
        <Card className="dark:bg-slate-800/40 dark:border-slate-700 overflow-hidden">
          <CardHeader className="bg-blue-600 dark:bg-blue-600 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                <CardTitle className="text-white">
                  Key Performance Indicators
                  <span className="ml-2 text-sm font-normal opacity-90">
                    ({timeRange === 'today' && 'Daily'}{timeRange === 'week' && 'Weekly'}{timeRange === 'month' && 'Monthly'}{timeRange === 'quarter' && 'Quarterly'}{timeRange === 'year' && 'Annual'})
                  </span>
                </CardTitle>
              </div>
              <button className="text-white/80 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
          </CardHeader>
          <CardContent className="p-6 dark:bg-slate-800/60">
            {/* Date Range Display */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-slate-400 dark:text-slate-400" />
                <span className="text-sm font-medium text-foreground dark:text-slate-200">
                  {timeRange === 'today' && 'Today'}
                  {timeRange === 'week' && 'This Week'}
                  {timeRange === 'month' && 'This Month'}
                  {timeRange === 'quarter' && 'This Quarter'}
                  {timeRange === 'year' && 'This Year'}
                </span>
                <span className="text-sm text-foreground dark:text-white">{new Date().toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground dark:text-slate-400">vs</span>
                <span className="text-sm text-foreground dark:text-white">
                  {timeRange === 'today' && 'Yesterday'}
                  {timeRange === 'week' && 'Last Week'}
                  {timeRange === 'month' && 'Last Month'}
                  {timeRange === 'quarter' && 'Last Quarter'}
                  {timeRange === 'year' && 'Last Year'}
                </span>
              </div>
              {(selectedMarket !== 'all' || selectedRep !== 'all') && (
                <Badge variant="outline" className="ml-auto dark:border-slate-600 dark:text-slate-300">
                  Filtered View
                </Badge>
              )}
            </div>

            {/* KPI Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <KPICard 
                title="NEW OPPORTUNITIES" 
                current={kpiStats.newOpportunities.current}
                previous={kpiStats.newOpportunities.previous}
                change={kpiStats.newOpportunities.change}
              />
              <KPICard 
                title="APPOINTMENTS SET" 
                current={kpiStats.appointmentsSet.current}
                previous={kpiStats.appointmentsSet.previous}
                change={kpiStats.appointmentsSet.change}
              />
              <KPICard 
                title="LEADS ISSUED" 
                current={kpiStats.leadsIssued.current}
                previous={kpiStats.leadsIssued.previous}
                change={kpiStats.leadsIssued.change}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <KPICard 
                title="DEMOS" 
                current={kpiStats.demos.current}
                previous={kpiStats.demos.previous}
                change={kpiStats.demos.change}
              />
              <KPICard 
                title="JOBS SOLD" 
                current={kpiStats.jobsSold.current}
                previous={kpiStats.jobsSold.previous}
                change={kpiStats.jobsSold.change}
              />
              <KPICard 
                title="PAYMENTS RECEIVED" 
                current={kpiStats.paymentsReceived.current}
                previous={kpiStats.paymentsReceived.previous}
                change={kpiStats.paymentsReceived.change}
              />
            </div>

            {/* Financial KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <KPICard 
                title="SOLD JOBS AMOUNT" 
                current={kpiStats.soldJobsAmount.current}
                previous={kpiStats.soldJobsAmount.previous}
                change={kpiStats.soldJobsAmount.change}
                isCurrency={true}
              />
              <KPICard 
                title="REVENUE AMOUNT" 
                current={kpiStats.revenueAmount.current}
                previous={kpiStats.revenueAmount.previous}
                change={kpiStats.revenueAmount.change}
                isCurrency={true}
              />
            </div>

            {/* Bottom Navigation */}
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-700">
              <div className="flex gap-6">
                <button className="text-sm font-medium text-blue-400 hover:text-blue-300">
                  Lead Performance
                </button>
                <button className="text-sm font-medium text-blue-400 hover:text-blue-300">
                  Sold Contracts
                </button>
                <button className="text-sm font-medium text-blue-400 hover:text-blue-300">
                  Payments
                </button>
              </div>
              <button className="text-slate-400 hover:text-slate-300">
                <Settings className="h-5 w-5" />
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Top Goals Section - Dark Mode */}
        {showTopGoals && (
          <Card className="dark:bg-slate-800/40 dark:border-slate-700 overflow-hidden">
            <CardHeader className="bg-blue-600 dark:bg-blue-600 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  <CardTitle className="text-white">Top Goals</CardTitle>
                </div>
                <button onClick={() => setShowTopGoals(false)} className="text-white/80 hover:text-white">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="p-8 dark:bg-slate-800/60">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg">
                    <Award className="h-12 w-12 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-foreground dark:text-white mb-3">
                    Stay on Track—Favorite Your Top Goals
                  </h3>
                  <p className="text-muted-foreground dark:text-slate-300 mb-3">
                    Favorite your most important goals to make it easier to monitor what matters. Keep them front and center on your dashboard.
                  </p>
                  <p className="text-muted-foreground dark:text-slate-300 mb-4">
                    Track progress at a glance and stay focused on what drives success.
                  </p>
                  <button className="text-blue-500 dark:text-blue-400 hover:text-blue-400 dark:hover:text-blue-300 font-semibold underline flex items-center gap-2">
                    Favorite My Top Goals
                    <CheckCircle className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Activity - Dark Mode */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="dark:bg-slate-800/60 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="dark:text-white">Recent Activity</CardTitle>
              <CardDescription className="dark:text-slate-400">Latest updates from your pipeline</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentActivity.length === 0 ? (
                  <p className="text-muted-foreground dark:text-slate-400 text-center py-8">No recent activity</p>
                ) : (
                  recentActivity.map((activity, index) => (
                    <div key={index} className="flex items-start gap-3 pb-3 border-b border-slate-700 last:border-b-0">
                      <div className="mt-1">
                        <Clock className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground dark:text-white">
                          {activity.customerName}
                        </p>
                        <p className="text-sm text-muted-foreground dark:text-slate-400 truncate">
                          {activity.note || activity.type}
                        </p>
                        <p className="text-xs text-muted-foreground dark:text-slate-500 mt-1">
                          {formatDate(activity.timestamp)}
                        </p>
                      </div>
                      <Badge variant={activity.type === 'stage_change' ? 'default' : 'secondary'} className="dark:bg-slate-700">
                        {activity.type}
                      </Badge>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Pipeline Breakdown - Dark Mode */}
          <Card className="dark:bg-slate-800/60 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="dark:text-white">Pipeline Breakdown</CardTitle>
              <CardDescription className="dark:text-slate-400">Opportunities by stage</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { stage: 'lead', label: 'New Leads', color: 'bg-slate-500' },
                  { stage: 'intro_call', label: 'Intro Calls', color: 'bg-blue-500' },
                  { stage: 'appointment_set', label: 'Appointments', color: 'bg-yellow-500' },
                  { stage: 'demo', label: 'Demos', color: 'bg-purple-500' },
                  { stage: 'pending_signature', label: 'Pending Signature', color: 'bg-orange-500' },
                  { stage: 'signed_agreement', label: 'Signed', color: 'bg-emerald-500' }
                ].map(({ stage, label, color }) => {
                  const count = jobs.filter(j => j.stage === stage).length;
                  const percentage = stats.totalLeads > 0 ? ((count / stats.totalLeads) * 100).toFixed(0) : 0;
                  
                  return (
                    <div key={stage} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-foreground dark:text-slate-200">{label}</span>
                        <span className="text-muted-foreground dark:text-slate-400">{count} ({percentage}%)</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div 
                          className={`${color} h-2 rounded-full transition-all`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Engagement Widgets Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CompetitionZoneWidget />
          <RhinoChallengesWidget />
        </div>
      </div>
    </Layout>
  );
};
